import { Fragment, useEffect, useRef, useState } from 'react';
import { ProtocolButton } from './ui';
import { EscalationPolicyButton } from '@/modules/escalation-policy/escalation-policy-button';
import type { AssignTarget, EscalationType } from '@/modules/escalation-policy/escalation-policy';

export interface FieldSpec {
  key: string;
  label: string;
  placeholder?: string;
  /** A message body: grows to fill the sheet. */
  multiline?: boolean;
  /** A pick-one field; the value saved is the option's value. */
  options?: { value: string; label: string }[];
  /** Shown only while another field holds this value, e.g. the agent pick while the target is Agent. */
  showWhen?: { key: string; value: string };
}

/**
 * A small escalation composer: a few labelled fields on the Send Email sheet's glass. Send Slack
 * Message uses it in protocols; Brand Settings uses it for every policy but email.
 */
export function ProtocolFieldsModal({
  title,
  fields,
  initial,
  policyType,
  policyTarget,
  validate,
  onSave,
  onClose
}: {
  title: string;
  fields: FieldSpec[];
  initial: Record<string, string>;
  /** Shows "View brand policy" for this escalation; omitted when the modal edits the policy itself. */
  policyType?: EscalationType;
  /** For an Assign: which target's default "View brand policy" shows, read off the values. */
  policyTarget?: (values: Record<string, string>) => AssignTarget;
  /** A refusal message, or null to save. */
  validate?: (values: Record<string, string>) => string | null;
  onSave: (values: Record<string, string>) => void;
  onClose: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLInputElement & HTMLTextAreaElement & HTMLSelectElement>(null);

  // A ref, so a parent re-render (a new onClose) neither re-binds Escape nor steals focus.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return;
      closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    first.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const set = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError(null);
  };
  const save = () => {
    const trimmed = Object.fromEntries(Object.entries(values).map(([key, value]) => [key, value.trim()]));
    const refusal = validate?.(trimmed) ?? null;
    if (refusal) return setError(refusal);
    onSave(trimmed);
  };

  const shown = fields.filter((field) => !field.showWhen || values[field.showWhen.key] === field.showWhen.value);

  const control = 'w-full bg-transparent text-[13px] leading-5 text-[var(--text-body)] outline-none placeholder:text-[var(--text-secondary)]';

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/25'
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role='dialog'
        aria-label={title}
        // A message field needs the room; a single pick sizes to its content.
        className={`${shown.some((field) => field.multiline) ? 'h-[360px]' : ''} flex max-h-[calc(100vh-32px)] w-188 max-w-[calc(100vw-32px)] flex-col overflow-clip rounded-2xl bg-[var(--glass-surface-light)] shadow-[0px_8px_32px_0px_rgba(0,0,0,0.12)] backdrop-blur-[20px]`}
      >
        <div className='flex h-12 shrink-0 items-center justify-between px-3'>
          <p className='text-sm font-semibold leading-5 text-[var(--text-body)]'>{title}</p>
          {policyType ? <EscalationPolicyButton type={policyType} target={policyTarget?.(values)} /> : null}
        </div>
        <div className='h-px w-full shrink-0 bg-[var(--border-default)]' />

        <div className='flex min-h-px flex-1 flex-col px-4 py-3'>
          {shown.map((field, index) => (
            <Fragment key={field.key}>
            {index > 0 ? <div className='my-2.5 h-px shrink-0 bg-[var(--border-subtle)]' /> : null}
            <label
              className={`flex gap-2 text-[13px] leading-5 ${field.multiline ? 'min-h-px flex-1 flex-col' : 'shrink-0 items-center'}`}
            >
              <span className='w-16 shrink-0 text-[var(--text-secondary)]'>{field.label}</span>
              {field.options ? (
                <select
                  ref={index === 0 ? first : undefined}
                  value={values[field.key] ?? ''}
                  onChange={(event) => set(field.key, event.target.value)}
                  className={`${control} h-6 cursor-pointer`}
                >
                  {field.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.multiline ? (
                <textarea
                  ref={index === 0 ? first : undefined}
                  value={values[field.key] ?? ''}
                  onChange={(event) => set(field.key, event.target.value)}
                  placeholder={field.placeholder}
                  className={`${control} protocol-scroll min-h-px flex-1 resize-none`}
                />
              ) : (
                <input
                  ref={index === 0 ? first : undefined}
                  value={values[field.key] ?? ''}
                  onChange={(event) => set(field.key, event.target.value)}
                  placeholder={field.placeholder}
                  className={`${control} h-6 min-w-0 flex-1`}
                />
              )}
            </label>
            </Fragment>
          ))}
        </div>

        <div className='flex shrink-0 items-center justify-end gap-3 px-4 pb-[15px] pt-2'>
          {error ? (
            <p role='alert' className='min-w-px flex-1 text-xs leading-4 text-[var(--text-error)]'>
              {error}
            </p>
          ) : null}
          <ProtocolButton variant='secondary' size='lg' onClick={onClose}>
            Cancel
          </ProtocolButton>
          <ProtocolButton variant='primary-dark' size='lg' onClick={save}>
            Save
          </ProtocolButton>
        </div>
      </div>
    </div>
  );
}
